import React from 'react';

import P from 'apps/Pdf/components/common/P';
import ChartOverview from 'apps/Pdf/components/InvestmentPlan/ChartOverview';
import PlanChart from 'apps/Pdf/components/Charts/PlanChart';
import Deposits from 'apps/Pdf/components/DespositsAndReserves/Deposits';
import GeneralDeposit from 'apps/Pdf/components/DespositsAndReserves/GeneralDeposit';
import Targets from 'apps/Pdf/components/Targets/Targets';
import FutureDeposits from 'apps/Pdf/components/DespositsAndReserves/FutureDeposits';
import Reserves from 'apps/Pdf/components/DespositsAndReserves/Reserves';
import { PersonType, Currency } from 'enums';
import { colors as colorDef, colorOpacity as colorOpacityDef } from 'apps/Pdf/styles/colors';
import { AccountEditPlan } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { TPL_DRAFT_OVERVIEW } from './Overview';
import { Flows } from 'pages/Accounts/Detail/enums';
import Composition from 'pages/Accounts/Edit/Plan/Composition';
import H2 from 'apps/Pdf/components/common/H2';
import PageBreak from 'apps/Pdf/components/common/PageBreak';
import ExternalProduct from 'pages/Accounts/Edit/components/ExternalProducts/ExternalProduct';

interface Props {
  data: AccountEditPlan;
  currency: Currency;
  change?: boolean;
  original?: boolean;
  flow: Flows;
}

const OverviewSection = (props: Props) => {
  const { currency, data, change, original, flow } = props;

  return (
    <>
      {flow !== Flows.WITHDRAWAL && (
        <>
          <ChartOverview data={data} currency={currency} templateType={TPL_DRAFT_OVERVIEW} change={change} />

          <PlanChart
            original={original}
            planData={data}
            rentApplied={!!data.rentApplied}
            currency={currency}
            portfolioOnly={data.portfolioOnly}
            showRange={data.rentApplied === false && data.targetData.targets.length === 0}
            xAxisTitle={
              data.personType === PersonType.LEGAL
                ? ''
                : `Věk klienta
          ${data.chart.metadata.partnerAgePresent ? ` / partnera` : ''}
        `
            }
            personType={data.personType}
            basicColors={{
              SHORT_TERM: colorDef.test.short,
              MID_TERM: colorDef.test.mid,
              LONG_TERM: colorDef.test.long,
              SHORT_TERM_ALT: colorDef.test.short_alt,
              MID_TERM_ALT: colorDef.test.mid_alt,
              LONG_TERM_ALT: colorDef.test.long_alt,
            }}
            colorOpacity={{
              SHORT_TERM: colorOpacityDef.test.short,
              MID_TERM: colorOpacityDef.test.mid,
              LONG_TERM: colorOpacityDef.test.long,
            }}
          />
        </>
      )}

      {data.externalProductData.externalProducts?.length !== 0 && ( //EDS-4891
        <>
          <PageBreak />
          <H2>Alokace majetku</H2>
          <Composition
            extProdData={data.externalProductData.externalProducts}
            composition={data.chart.composition}
            finInst={data.chart.metadata.financialInstitutions}
            original={original}
            pdf
          />
          <PageBreak />
        </>
      )}

      <Deposits deposits={data.depositData} currency={currency} actualInflation={0} original={original} />

      {data.depositData.employerAllowed !== undefined && (
        <P>
          {data.depositData.employerAllowed === false && 'Klient nesouhlasí s vklady od zaměstnavatele'}
          {data.depositData.employerAllowed === true && 'Klient souhlasí s vklady od zaměstnavatele'}
        </P>
      )}
      {data.depositData.employerAllowed && data.depositData.employer && (
        <GeneralDeposit
          deposit={data.depositData.employer}
          currency={currency}
          actualInflation={0}
          type="employer"
          original={original}
        />
      )}

      <FutureDeposits data={data.depositData.deposits} currency={currency} actualInflation={0} original={original} />

      <Reserves data={data.reserveData} currency={currency} original={original} />

      <Targets
        data={data.targetData?.targets}
        rentData={data.rentData}
        currency={currency}
        personType={data.personType}
        actualInflation={0}
        original={original}
        hideInfo
      />

      {data.externalProductData?.externalProducts?.length !== 0 && (
        <>
          <H2>Další zdroje</H2>
          <div style={{ margin: '0 -8px', display: 'flex', flexWrap: 'wrap' }}>
            {data.externalProductData.externalProducts.map((p) => (
              <ExternalProduct data={p} key={p.id} pdf original={original} category={p.category} />
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default OverviewSection;
